import { useState } from 'react'
import PaginationControls, { paginateItems } from '../components/PaginationControls'
import { useApp } from '../context/AppContext'
import { formatDatetime } from '../utils'

const ACAO_LABEL: Record<string, string> = {
  CRIADO: 'Criado',
  EDITADO: 'Editado',
  APROVADO: 'Aprovado',
  REJEITADO: 'Rejeitado',
  CANCELADO: 'Cancelado',
  PAGO: 'Pago',
}

const ACAO_COLOR: Record<string, string> = {
  CRIADO: 'bg-blue-500/10 text-blue-400',
  EDITADO: 'bg-gray-500/10 text-gray-400',
  APROVADO: 'bg-emerald-500/10 text-emerald-400',
  REJEITADO: 'bg-red-500/10 text-red-400',
  CANCELADO: 'bg-orange-500/10 text-orange-400',
  PAGO: 'bg-cyan-500/10 text-cyan-400',
}

const ENTIDADE_LABEL: Record<string, string> = {
  REQUISICAO: 'Requisição',
  COTACAO: 'Cotação',
  ORDEM_COMPRA: 'Ordem de Compra',
  CONTA_PAGAR: 'Conta a Pagar',
  PAGAMENTO: 'Pagamento',
  OBRA: 'Obra',
  CLIENTE: 'Cliente',
  FORNECEDOR: 'Fornecedor',
  COLABORADOR: 'Colaborador',
}

export default function Auditoria() {
  const { logs } = useApp()
  const [filtroAcao, setFiltroAcao] = useState('')
  const [filtroTipo, setFiltroTipo] = useState('')
  const [busca, setBusca] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const tiposUnicos = Array.from(new Set(logs.map(l => l.entidade_tipo)))

  const lista = logs.filter(l => {
    if (filtroAcao && l.acao !== filtroAcao) return false
    if (filtroTipo && l.entidade_tipo !== filtroTipo) return false
    if (busca) {
      const q = busca.toLowerCase()
      if (
        !l.usuario_nome.toLowerCase().includes(q) &&
        !l.detalhes.toLowerCase().includes(q) &&
        !l.entidade_id.toLowerCase().includes(q)
      ) return false
    }
    return true
  })
  const paginated = paginateItems(lista, page, pageSize)

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-white">Auditoria</h1>
          <p className="text-xs text-gray-500 mt-0.5">Trilha de ações relevantes do sistema</p>
        </div>
        <span className="text-xs text-gray-600">{lista.length} registros</span>
      </div>

      {/* Filtros */}
      <div className="flex gap-3 mb-4">
        <input
          className="input-field w-52"
          placeholder="Buscar usuário, detalhe..."
          value={busca}
          onChange={e => { setBusca(e.target.value); setPage(1) }}
        />
        <select className="input-field w-36" value={filtroAcao} onChange={e => { setFiltroAcao(e.target.value); setPage(1) }}>
          <option value="">Todas as ações</option>
          {Object.entries(ACAO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="input-field w-44" value={filtroTipo} onChange={e => { setFiltroTipo(e.target.value); setPage(1) }}>
          <option value="">Todos os módulos</option>
          {tiposUnicos.map(t => <option key={t} value={t}>{ENTIDADE_LABEL[t] || t}</option>)}
        </select>
      </div>

      {/* Timeline */}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/[0.06]">
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Data / Hora</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Módulo</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Ação</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Usuário</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Detalhes</th>
            </tr>
          </thead>
          <tbody>
            {lista.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-gray-600 text-sm">
                  Nenhum log encontrado.
                </td>
              </tr>
            ) : paginated.items.map((l, index) => (
              <tr key={`${l.id}-${l.createdAt}-${index}`} className="table-row-hover border-b border-white/[0.04]">
                <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{formatDatetime(l.createdAt)}</td>
                <td className="px-4 py-3">
                  <span className="text-xs text-gray-400 bg-white/[0.04] px-2 py-0.5 rounded">
                    {ENTIDADE_LABEL[l.entidade_tipo] || l.entidade_tipo}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`status-badge ${ACAO_COLOR[l.acao] || 'bg-gray-500/10 text-gray-400'}`}>
                    {ACAO_LABEL[l.acao] || l.acao}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-300 text-xs">{l.usuario_nome}</td>
                <td className="px-4 py-3 text-gray-500 text-xs max-w-[300px]">{l.detalhes}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationControls
          page={paginated.safePage}
          pageSize={pageSize}
          totalItems={lista.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>
    </div>
  )
}
